// Surviving the reload that linking an embedded camera forces (ENG-4649).
//
// `credentials-dialog.tsx` calls window.location.reload() on a successful
// direct link, and has since ENG-3057 (#811) — it is how an embedded camera is
// made to reappear in the source tray after a re-link. Everything the funnel
// knows about the run in progress is React state in that renderer, so the
// reload throws it away: the user linked their first camera and then either got
// the welcome screen again (the ENG-4649 report) or, once the link records
// onboarding, no funnel at all. Neither finishes the flow they started.
//
// So the funnel writes down where it was, and picks the run back up after the
// reload. Deliberately NOT a general "resume onboarding" feature: it is scoped
// to one reload, by a short expiry, and it is cleared the moment the funnel
// reaches a terminal screen.
//
// localStorage rather than the app's electron-store: this has to be readable
// synchronously on the very first render after the reload, before any IPC
// round-trip could answer, and it is per-renderer state that must not outlive
// the machine it was written on.
//
// Per rules: think-in-systems, code-comments-required,
// reference-rules-in-comments (ai.knowledge/global).

const KEY = 'iris.onboarding.resume.v1';

/**
 * How long a resume record stays good.
 *
 * It exists to cross ONE page reload, which takes seconds. A stale record is
 * worse than none: it would reopen the funnel over a studio the user has since
 * been working in. Generous enough for a slow relink, far short of a session.
 */
export const RESUME_TTL_MS = 120_000;

export interface OnboardingResume {
  /** The agent whose link triggered the reload. */
  readonly agentId: string;
  /** Wall clock at write time; the record expires RESUME_TTL_MS later. */
  readonly startedAt: number;
  /** Camera name already known before the reload. The magic moment paints this immediately. */
  readonly name?: string;
  /** Camera address already known before the reload. */
  readonly host?: string;
}

/**
 * Note that an embedded link is about to reload the page mid-funnel.
 *
 * Called from the link path, which knows the agent; the funnel supplies the
 * "am I open" answer through `active`, so the dialog never has to reason about
 * onboarding. A false `active` clears rather than writes: a link performed
 * outside the funnel must not leave a record that resumes it later.
 */
export const noteEmbeddedLinkForOnboarding = (
  agentId: string,
  active: boolean,
  camera?: { readonly name?: string; readonly host?: string }
): void => {
  try {
    if (!active || agentId === '') {
      window.localStorage.removeItem(KEY);
      return;
    }

    const record: OnboardingResume = {
      agentId,
      startedAt: Date.now(),
      ...(typeof camera?.name === 'string' && camera.name !== '' ? { name: camera.name } : {}),
      ...(typeof camera?.host === 'string' && camera.host !== '' ? { host: camera.host } : {})
    };
    window.localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    // Storage can throw (private mode, blocked site data). Losing the record
    // costs the resume, not the link, so this stays silent.
  }
};

/** The pending resume, or undefined when there is none or it has expired. */
export const readOnboardingResume = (now: number = Date.now()): OnboardingResume | undefined => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return undefined;

    const parsed = JSON.parse(raw) as Partial<OnboardingResume>;
    if (typeof parsed.agentId !== 'string' || typeof parsed.startedAt !== 'number') {
      return undefined;
    }
    if (now - parsed.startedAt > RESUME_TTL_MS) return undefined;

    const name = typeof parsed.name === 'string' && parsed.name !== '' ? parsed.name : undefined;
    const host = typeof parsed.host === 'string' && parsed.host !== '' ? parsed.host : undefined;

    return {
      agentId: parsed.agentId,
      startedAt: parsed.startedAt,
      ...(name !== undefined ? { name } : {}),
      ...(host !== undefined ? { host } : {})
    };
  } catch {
    return undefined;
  }
};

/** Drop the record. Called once the funnel has consumed it or has finished. */
export const clearOnboardingResume = (): void => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // See noteEmbeddedLinkForOnboarding.
  }
};
